import type { CalculatorArticleContent } from "../calculator-article";

export const waterGuide: CalculatorArticleContent = {
  id: "water",
  eyebrow: "Hidrasyon rehberi",
  updatedAt: "29 Eylül 2026",
  articleTitle: "Günlük su ihtiyacını planlamak ve antrenmana uyarlamak",
  intro:
    "Su ihtiyacı herkes için sabit bir litre değildir. Vücut ağırlığına dayalı hesap, günlük içecek hedefi için başlangıç verir; hava, terleme, egzersiz süresi, beslenme, sağlık ve kişisel tolerans bu hedefi değiştirir. Amaç mümkün olan en çok suyu içmek değil, kayıpları uygun biçimde karşılamaktır.",
  sections: [
    {
      id: "toplam-su-alimi",
      title: "Toplam su alımı neyi kapsar?",
      paragraphs: [
        "Bilimsel referanslarda toplam su alımı yalnız bardaktan içilen sade suyu değil, diğer içecekleri ve yiyeceklerdeki suyu da kapsar. Meyve, sebze, çorba, süt ve birçok içecek günlük su alımına katkı sağlar. Bu nedenle toplam su referansını doğrudan sadece sade su hedefi gibi uygulamak gereksiz yüksek sonuca yol açabilir.",
        "Hesaplayıcı vücut ağırlığından pratik bir içecek hedefi üretir. Bu hedef kişisel ölçüm değildir ve her gün aynı olmak zorunda değildir. Dinlenme günüyle sıcak ortamda uzun antrenman yaptığın günün gereksinimi farklı olabilir.",
        "EFSA yetişkinler için toplam suya ilişkin nüfus düzeyi referans değerleri sunar; bunlar yeterli alım değerleridir, kişiye özel reçete değildir. Cinsiyet, gebelik, emzirme, iklim ve fiziksel aktivite bağlamı ayrıca değerlendirilir.",
      ],
      sourceIds: ["1", "2"],
    },
    {
      id: "gunluk-hedef",
      title: "Hesaplanan günlük hedefi nasıl kullanmalısın?",
      paragraphs: [
        "Sonucu güne dağıt. Uzun süre hiç içmeden akşam kısa sürede büyük hacim tüketmek yerine öğünler ve aktivite çevresinde düzenli alım daha rahattır. Susama sinyali çoğu sağlıklı yetişkin için yararlı olsa da sıcak ortam, yoğun egzersiz, yaş ve bazı sağlık durumları bu sinyali etkileyebilir.",
        "İdrar rengi tek başına laboratuvar ölçümü değildir; fakat susama ve vücut ağırlığı değişimiyle birlikte pratik geri bildirim sunabilir. Çok açık idrar her zaman 'daha iyi', koyu idrar da her durumda ciddi susuzluk anlamına gelmez. Vitaminler, ilaçlar ve ilk sabah örneği rengi etkileyebilir.",
      ],
      steps: [
        "Hesaplanan değeri normal bir dinlenme günü için başlangıç kabul et.",
        "Suyu gün içine ve öğünlere dağıt; tek seferde zorlayarak içme.",
        "Susama, idrar rengi, baş ağrısı ve performans gibi işaretleri birlikte izle.",
        "Sıcaklık, terleme ve egzersiz süresi arttığında planı kişisel kayba göre ayarla.",
        "Sağlık durumun veya sıvı kısıtlaman varsa genel hesabı değil hekim önerisini uygula.",
      ],
    },
    {
      id: "egzersiz-hidrasyonu",
      title: "Antrenman öncesi, sırası ve sonrasında hidrasyon",
      paragraphs: [
        "Egzersizde hedef, performansı bozacak kadar sıvı kaybını ve gereğinden fazla içmeyi önlemektir. Terleme hızı kişiden kişiye büyük değişir; aynı salonda aynı antrenmanı yapan iki sporcu farklı miktarda sıvı kaybedebilir. Kıyafet, sıcaklık, nem, kondisyon ve genetik terlemeyi etkiler.",
        "Kişisel terleme hızını yaklaşık görmek için antrenman öncesi ve sonrası benzer kıyafetle tartılabilirsin. İçtiğin sıvıyı ve idrarı hesaba katarak saatlik kaybı tahmin etmek mümkündür. Amaç antrenman sonunda tam olarak aynı kiloda olmak zorunda değildir; aşırı içerek başlangıç ağırlığının üzerine çıkmak risklidir.",
        "Uzun süren, sıcak veya yoğun terlemeli seanslarda yalnız su değil sodyum kaybı da önem kazanabilir. Elektrolit gereksinimi terleme miktarı, seans süresi ve beslenmeye göre değişir. Her kısa salon antrenmanında özel sporcu içeceği gerekli değildir.",
      ],
      table: {
        caption: "Egzersiz hidrasyonunu kişiselleştiren değişkenler",
        headers: ["Değişken", "Ne değişir?", "Nasıl takip edilir?"],
        rows: [
          [
            "Sıcaklık ve nem",
            "Terleme ve buharlaşma",
            "Ortam koşulunu ve performans hissini kaydet",
          ],
          [
            "Seans süresi",
            "Toplam sıvı ve elektrolit kaybı",
            "Başlangıç-bitiş saati ve içilen hacim",
          ],
          [
            "Terleme hızı",
            "Kişisel saatlik gereksinim",
            "Antrenman öncesi/sonrası vücut ağırlığı",
          ],
          [
            "Sodyum kaybı",
            "Uzun seanslarda elektrolit ihtiyacı",
            "Tuzlu ter izleri, süre ve profesyonel değerlendirme",
          ],
        ],
      },
      sourceIds: ["3", "4"],
    },
    {
      id: "fazlasi-daha-iyi-degil",
      title: "Fazlası neden her zaman daha iyi değildir?",
      paragraphs: [
        "Kısa sürede böbreklerin atabileceğinden fazla su içmek kandaki sodyumu seyreltebilir ve egzersizle ilişkili hiponatremiye yol açabilir. Baş ağrısı, bulantı, kafa karışıklığı ve ağır vakalarda nörolojik belirtiler oluşabilir. Bu nedenle 'ne kadar çok, o kadar iyi' yaklaşımı güvenli değildir.",
        "Özellikle uzun dayanıklılık etkinliklerinde planı yalnız susuz kalma korkusuyla kurmak aşırı içmeye neden olabilir. Antrenman boyunca vücut ağırlığının artması, gereksinimin üzerinde sıvı alındığına işaret edebilir. Kişisel terleme hızına dayalı yaklaşım bu iki ucu dengelemeye yardımcı olur.",
        "Kalp, böbrek veya karaciğer hastalığı, düşük sodyum öyküsü ya da diüretik kullanımı sıvı yönetimini değiştirir. Bu durumlarda genel hesaplayıcı hedefi uygun olmayabilir; hekim tarafından verilen sıvı veya sodyum kısıtlaması önceliklidir.",
      ],
      note: "Şiddetli baş ağrısı, bilinç bulanıklığı, durmayan kusma veya nöbet acil değerlendirme gerektirir. Bu sayfa tanı ve tedavi aracı değildir.",
      sourceIds: ["3"],
    },
    {
      id: "icecekler-besinler",
      title: "Kahve, çay ve besinlerdeki su sayılır mı?",
      paragraphs: [
        "Çay, kahve, süt ve şekersiz içecekler sıvı alımına katkı sağlar. Kafein hafif diüretik etki gösterebilse de alışılmış miktarlardaki kafeinli içecekler net sıvı kaybı yaratmak zorunda değildir. Yine de yüksek kafein uyku, kalp çarpıntısı ve bireysel tolerans açısından ayrı değerlendirilmelidir.",
        "Meyve, sebze, yoğurt ve çorba gibi besinler de su sağlar. Beslenmesi su içeriği yüksek gıdalardan oluşan biri, aynı toplam su alımına daha az içecekle ulaşabilir. Kuru ve tuzlu beslenen birinin içecek gereksinimi farklı hissedilebilir.",
        "Şekerli içecekler hidrasyona katkı sağlasa da ek enerji ve diş sağlığı açısından sade suyla aynı seçenek değildir. İçecek tercihini yalnız sıvı hacmi değil, kalori, kafein, alkol ve sağlık hedefleriyle birlikte düşün.",
      ],
      sourceIds: ["2"],
    },
    {
      id: "yaygin-hatalar",
      title: "Sık yapılan hidrasyon hataları",
      paragraphs: [
        "Günlük su hedefini kesin kota gibi görmek, hem gereksiz zorlamaya hem de egzersiz günlerinde yetersiz kalmaya yol açabilir. Şu hataları azalt.",
      ],
      bullets: [
        "Toplam su referansının tamamını yalnız sade su olarak içmeye çalışmak.",
        "Vücut ağırlığı formülünü sıcaklık ve terlemeden bağımsız kesin ihtiyaç sanmak.",
        "Uzun süre içmeyip gün sonunda kısa sürede litrelerce su tüketmek.",
        "Her antrenmana aynı sıvı ve elektrolit planını uygulamak.",
        "Antrenman sonunda başlangıç ağırlığının üzerine çıkacak kadar içmek.",
        "Koyu veya açık idrar rengini tek başına tanı koydurucu kabul etmek.",
        "Hekimin verdiği sıvı kısıtlaması varken genel hesaplayıcı sonucunu uygulamak.",
      ],
    },
    {
      id: "uygulama-ornegi",
      title: "Uygulama örneği: terleme hızına göre plan yapmak",
      paragraphs: [
        "Antrenmandan hemen önce 80,0 kg, bir saatlik seans sonunda 79,2 kg olduğunu ve bu sırada 500 ml su içtiğini düşün. Tuvalet kaybı yoksa yaklaşık toplam sıvı kaybın 1,3 litredir: 0,8 kg vücut ağırlığı düşüşü ile içilen 0,5 litrenin toplamı. Bu, benzer ortam ve antrenmanda yaklaşık saatlik terleme hızına dair pratik tahmin verir.",
        "Bu sayı bir sonraki seans boyunca mutlaka 1,3 litre içmen gerektiği anlamına gelmez. Mide toleransı, başlangıç hidrasyonu, sıcaklık ve seans süresi planı etkiler. Ama 250 ml ile bitirdiğinde belirgin performans düşüşü yaşadığını görüyorsan, alımı kademeli artırmak için kişisel dayanak sağlar.",
        "Aynı hesabı serin ve sıcak günlerde ayrı yapmak önemlidir. Sıcak ortamda terleme hızı yükselirken serin günde aynı plan gereksiz olabilir. Uzun seanslarda çok terliyor, kıyafette belirgin tuz izi görüyor veya sık kramp yaşıyorsan elektrolit planı için uygun profesyonel değerlendirme yararlı olabilir.",
        "Seans sonunda vücut ağırlığın başlangıcın üzerine çıkıyorsa gereksinimin üzerinde içmiş olabilirsin. Baş ağrısı veya bulantıyı yalnız susuzluk sanıp daha fazla su içmek yerine aşırı alım ihtimalini de düşün. Amaç sıfır kayıp zorunluluğu değil; performansı koruyan, rahat ve güvenli kişisel plan oluşturmaktır.",
      ],
      sourceIds: ["3", "4"],
    },
    {
      id: "ozel-kosullar",
      title: "İklim, yolculuk ve özel koşullarda planı ayarlamak",
      paragraphs: [
        "Sıcak ve nemli ortam terlemeyi artırır; yüksek nem terin buharlaşmasını zorlaştırdığı için vücut aynı serinleme etkisi için daha fazla zorlanabilir. Soğuk havada susama azalabilir, fakat kalın kıyafet ve kuru hava yine sıvı kaybı yaratır. Günlük hedefi yalnız mevsim adına göre değil, gerçek ortam ve aktiviteye göre ayarla.",
        "Uçak yolculuğu ve yüksek rakımda kuru hava, rutin dışı yemek ve hareket düzeni susama hissini değiştirebilir. Bunun çözümü tek seferde aşırı su içmek değil, yolculuk boyunca düzenli alım ve idrar/susama işaretlerini izlemektir. Alkol uykuyu ve toparlanmayı etkileyebilir; sıvı içeriyor diye hidrasyon stratejisi sayılmaz.",
        "Hastalıkta ateş, kusma veya ishal sıvı ve elektrolit kaybını artırabilir. Bu durumda spor için hazırlanmış genel ml/kg hesabı yeterli olmayabilir. Sıvı tutamama, belirgin halsizlik, baş dönmesi veya çok az idrar gibi belirtilerde tıbbi değerlendirme gerekir.",
      ],
      subsections: [
        {
          title: "Gebelik, emzirme ve ileri yaş",
          paragraphs: [
            "Gebelik ve emzirme toplam su ihtiyacını değiştirebilir; yaşla birlikte susama algısı da zayıflayabilir. Nüfus referansları başlangıç bilgisi sunar, fakat eşlik eden sağlık durumu ve kullanılan ilaçlar kişisel öneriyi belirler. Bu gruplarda genel spor hesaplayıcısını tek rehber yapma.",
          ],
        },
        {
          title: "Kilo sınıfı sporcusunda tartı sonrası sıvı",
          paragraphs: [
            "Tartı için bilinçli sıvı kısıtlaması yüksek risk taşıyabilir. Yeniden hidrasyon yalnız su hacmi değil, sodyum, karbonhidrat, süre ve mide toleransı gerektirir. Hızlı kilo kesimi planını genel günlük su hedefinden türetme; deneyimli spor hekimi ve beslenme uzmanı gözetimi gerekir.",
          ],
        },
      ],
      sourceIds: ["1", "3"],
    },
  ],
  faqs: [
    {
      question: "Çay ve kahve günlük sıvıya dahil mi?",
      answer:
        "Evet, genellikle sıvı alımına katkı sağlar. Kafein miktarı, uyku ve kişisel tolerans ayrıca dikkate alınmalıdır.",
    },
    {
      question: "Yiyeceklerdeki su hesaba katılır mı?",
      answer:
        "Evet. Bilimsel toplam su alımı tanımı içeceklerle birlikte besinlerdeki suyu da kapsar.",
    },
    {
      question: "Antrenmanda ne kadar su içmeliyim?",
      answer:
        "Süre, sıcaklık, nem ve kişisel terleme hızına göre değişir. Uzun veya sıcak seanslarda antrenman öncesi-sonrası ağırlık takibi kişisel plan için daha yararlıdır.",
    },
    {
      question: "İdrar rengi yeterli bir gösterge mi?",
      answer:
        "Tek başına kesin değildir. Susama, koşullar ve vücut ağırlığı değişimiyle birlikte pratik bir işaret olarak kullanılabilir.",
    },
    {
      question: "Çok fazla su içmek zararlı olabilir mi?",
      answer:
        "Evet. Kısa sürede aşırı tüketim kandaki sodyumu seyrelterek hiponatremi riskini artırabilir.",
    },
    {
      question: "Elektrolit içeceği her antrenmanda gerekli mi?",
      answer:
        "Hayır. Gereksinim seans süresi, sıcaklık, terleme ve beslenmeye bağlıdır. Kısa ve serin ortam antrenmanlarında sade su çoğu kişi için yeterli olabilir.",
    },
    {
      question: "Sabah kalkınca mutlaka büyük miktar su içmeli miyim?",
      answer:
        "Belirli bir büyük hacim zorunlu değildir. Susamana ve günün planına göre rahat bir miktarla başlayıp alımı güne dağıtabilirsin.",
    },
    {
      question: "Hangi durumda hesaplayıcıyı kullanmamalıyım?",
      answer:
        "Hekim tarafından sıvı kısıtlaması verildiyse veya kalp, böbrek, karaciğer hastalığın varsa genel hedef yerine kişisel tıbbi öneriyi uygula.",
    },
    {
      question: "Antrenman öncesi ve sonrası tartı neyi gösterir?",
      answer:
        "İçilen sıvı ve varsa idrarı hesaba kattığında yaklaşık terleme hızını tahmin etmene yardım eder. Tek seans yerine benzer koşullardaki birkaç ölçümle kişisel aralık oluştur.",
    },
    {
      question: "Antrenman sonunda neden daha ağır olmamalıyım?",
      answer:
        "Başlangıçtan daha ağır bitirmek, kayıptan fazla sıvı aldığını gösterebilir. Özellikle uzun egzersizde aşırı içme kandaki sodyumu seyreltebilir; sıfır susama uğruna zorlayarak içme.",
    },
    {
      question: "Maden suyu elektrolit için yeterli mi?",
      answer:
        "Ürünün sodyum ve mineral içeriğine, kaybın büyüklüğüne ve beslenmene bağlıdır. Her maden suyu aynı değildir; uzun ve yoğun terlemeli etkinlik için kişisel elektrolit planının otomatik karşılığı sayılmaz.",
    },
  ],
  sources: [
    {
      id: "1",
      label: "EFSA — su için diyet referans değerleri özeti",
      href: "https://www.efsa.europa.eu/sites/default/files/2017_09/DRVs_summary_report.pdf",
    },
    {
      id: "2",
      label: "CDC — su ve daha sağlıklı içecekler",
      href: "https://www.cdc.gov/healthy-weight-growth/water-healthy-drinks/index.html",
    },
    {
      id: "3",
      label:
        "National Athletic Trainers' Association — sıvı replasmanı pozisyon bildirisi",
      href: "https://pubmed.ncbi.nlm.nih.gov/28985128/",
    },
    {
      id: "4",
      label:
        "American College of Sports Medicine — egzersiz ve sıvı replasmanı",
      href: "https://pubmed.ncbi.nlm.nih.gov/17277604/",
    },
  ],
  related: [
    {
      href: "/essay/calori-calculator",
      label: "Kalori ihtiyacını hesapla",
      description:
        "Günlük enerji hedefini hidrasyon planınla birlikte değerlendir.",
    },
    {
      href: "/essay/body-fat-calculator",
      label: "Yağ oranını hesapla",
      description: "Vücut kompozisyonu eğilimini aynı ölçüm yöntemiyle izle.",
    },
  ],
  primaryCta: {
    href: "/coachs",
    label: "Koçluk seçeneklerini incele",
    description:
      "Antrenman, beslenme ve toparlanmayı kişisel verilerinle planla.",
  },
};
